import { useEffect, useState } from 'react';
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Award,
  BarChart3,
  Ban,
  Bike,
  Building2,
  Boxes,
  CalendarClock,
  ClipboardList,
  Headset,
  LayoutDashboard,
  LifeBuoy,
  Megaphone,
  MapPinned,
  LogOut,
  type LucideIcon,
  Menu as MenuIcon,
  Package,
  Radio,
  Receipt,
  ScrollText,
  Settings,
  ShieldCheck,
  Star,
  Store,
  Ticket,
  Truck,
  Users,
  Wallet,
  X,
} from 'lucide-react';
import { MerchantStatus, UserRole, WS_EVENT, type SupportMessagePayload } from '@kick/types';
import { api } from '@/lib/api';
import { esAdmin, useAuth } from '@/lib/auth-store';
import { MODULO_POR_RUTA, useAccesoTotal, usePuede } from '@/lib/permisos';
import { cn } from '@/lib/cn';
import {
  useAlternarApertura,
  useComercio,
  useEncomiendasPendientes,
  useRecargasPendientes,
  useManualesPendientes,
  useSoporteSinLeer,
  useSoporteSinLeerComercio,
} from '@/lib/queries';
import { cerrarSocket, useEventoSocket } from '@/lib/socket';
import { sonarAviso } from '@/lib/sonido';
import { Interruptor } from './ui';
import { useAvisos } from './avisos';
import { BarreraError } from './BarreraError';
import { HealthBadge } from './HealthBadge';
import { Campana } from './Campana';

interface Enlace {
  a: string;
  texto: string;
  icono: LucideIcon;
  /** Todavía no implementado: se muestra deshabilitado con su fase. */
  fase?: number;
}

const ENLACES_COMERCIO: Enlace[] = [
  { a: '/', texto: 'Resumen', icono: LayoutDashboard },
  { a: '/menu', texto: 'Menú', icono: Package },
  { a: '/pedidos', texto: 'Pedidos', icono: ClipboardList },
  { a: '/envios', texto: 'Envíos', icono: Bike },
  { a: '/calificaciones', texto: 'Calificaciones', icono: Star },
  { a: '/cupones', texto: 'Cupones', icono: Ticket },
  { a: '/informes', texto: 'Informes', icono: BarChart3 },
  { a: '/cuenta', texto: 'Mi cuenta', icono: Wallet },
  { a: '/perfil', texto: 'Mi comercio', icono: Store },
  { a: '/usuarios', texto: 'Usuarios', icono: Users },
  { a: '/soporte', texto: 'Soporte', icono: Headset },
];

const ENLACES_ADMIN: Enlace[] = [
  { a: '/', texto: 'Resumen', icono: LayoutDashboard },
  { a: '/operacion', texto: 'Operación', icono: ClipboardList },
  { a: '/programadas', texto: 'Programadas', icono: CalendarClock },
  { a: '/manuales', texto: 'Asignación manual', icono: Boxes },
  { a: '/comercios', texto: 'Comercios', icono: Building2 },
  { a: '/flota', texto: 'Flota en el mapa', icono: Radio },
  { a: '/domiciliarios', texto: 'Domiciliarios', icono: Truck },
  { a: '/usuarios-plataforma', texto: 'Usuarios', icono: Users },
  { a: '/zonas', texto: 'Zonas y tarifas', icono: MapPinned },
  { a: '/calificaciones', texto: 'Calificaciones', icono: Star },
  { a: '/clasificacion', texto: 'Clasificación', icono: Award },
  { a: '/cupones', texto: 'Cupones', icono: Ticket },
  { a: '/informes-plataforma', texto: 'Informes', icono: BarChart3 },
  { a: '/cobros', texto: 'Cobros', icono: Wallet },
  { a: '/liquidaciones', texto: 'Liquidaciones', icono: Receipt },
  { a: '/anuncios', texto: 'Anuncios', icono: Megaphone },
  { a: '/soporte', texto: 'Soporte', icono: Headset },
  { a: '/reclamos', texto: 'Reclamos', icono: LifeBuoy },
  { a: '/auditoria', texto: 'Auditoría', icono: ScrollText },
  { a: '/ajustes', texto: 'Ajustes', icono: Settings },
  /** Solo para quien tiene acceso total: crear administradores y darles permisos. */
  { a: '/equipo', texto: 'Equipo', icono: ShieldCheck },
];

export function Layout() {
  const { user } = useAuth();
  const admin = esAdmin(user?.role);
  const puede = usePuede();
  const accesoTotal = useAccesoTotal();
  const [menuAbierto, setMenuAbierto] = useState(false);

  /**
   * Un administrador con permisos detallados solo ve en el menú los módulos
   * a los que puede entrar. Lo que no se ve no se extraña: una entrada gris
   * que dice «sin permiso» invita a pedir permisos que el dueño no quiso dar.
   */
  const enlaces = admin
    ? ENLACES_ADMIN.filter(({ a }) => {
        if (a === '/equipo') return accesoTotal;
        const modulo = MODULO_POR_RUTA[a];
        return !modulo || puede(modulo, 'ver');
      })
    : ENLACES_COMERCIO;

  return (
    <div className="min-h-dvh bg-kick-light">
      <BarraSuperior onAlternarMenu={() => setMenuAbierto((v) => !v)} menuAbierto={menuAbierto} />

      <div className="mx-auto flex max-w-[1600px]">
        <BarraLateral enlaces={enlaces} abierta={menuAbierto} onCerrar={() => setMenuAbierto(false)} />

        <main className="min-w-0 flex-1 px-4 py-6 sm:px-6 lg:px-8">
          {!admin && <AvisoSuspension />}

          {/* La barrera va aquí dentro: si una pantalla falla, la barra
              superior sigue viva y se puede navegar o cerrar sesión. */}
          <BarreraError>
            <Outlet />
          </BarreraError>
        </main>
      </div>
    </div>
  );
}

/**
 * Por qué el comercio no está vendiendo.
 *
 * Va encima de todas sus pantallas, no escondido en una: alguien que entra un
 * lunes y no ve pedidos necesita saber por qué en el primer segundo, y
 * «suspendido» a secas dejaría a esa persona llamando para preguntar qué pasó.
 * Con el motivo delante y el enlace a su cuenta, se resuelve solo.
 */
function AvisoSuspension() {
  const merchantId = useAuth((s) => s.merchantId);
  const { data: comercio } = useComercio(merchantId ?? '');

  if (!comercio || comercio.status !== MerchantStatus.SUSPENDED) return null;

  return (
    <div className="mb-5 flex flex-wrap items-start gap-3 rounded-kick border border-red-200 bg-red-50 p-4">
      <Ban className="mt-0.5 h-5 w-5 shrink-0 text-red-600" />
      <div className="min-w-0 flex-1">
        <p className="font-bold text-red-900">Tu tienda está suspendida</p>
        <p className="mt-0.5 text-sm text-red-800">
          {comercio.suspensionReason ?? 'Comunícate con administración.'} Mientras tanto no recibes
          pedidos ni apareces en la tienda.
        </p>
      </div>
      <NavLink
        to="/cuenta"
        className="shrink-0 rounded-kick bg-red-600 px-3 py-2 text-sm font-semibold text-white transition hover:bg-red-700"
      >
        Ver mi cuenta
      </NavLink>
    </div>
  );
}

function BarraSuperior({
  onAlternarMenu,
  menuAbierto,
}: {
  onAlternarMenu: () => void;
  menuAbierto: boolean;
}) {
  const { user, merchantId, cerrar } = useAuth();
  const admin = esAdmin(user?.role);
  const navegar = useNavigate();

  const salir = useMutation({
    mutationFn: async () => {
      await api.post('/auth/logout', {});
    },
    // Aunque el servidor falle, la sesión local se cierra igual.
    onSettled: () => {
      cerrar();
      // El canal en vivo lleva el token de quien salía: se cierra con él.
      cerrarSocket();
      navegar('/login');
    },
  });

  return (
    <header className="sticky top-0 z-30 border-b border-kick-gray/15 bg-kick-dark">
      <div className="mx-auto flex max-w-[1600px] items-center gap-3 px-4 py-3">
        <button
          onClick={onAlternarMenu}
          aria-label={menuAbierto ? 'Cerrar menú' : 'Abrir menú'}
          className="rounded-kick p-2 text-white/80 transition hover:bg-white/10 lg:hidden"
        >
          {menuAbierto ? <X className="h-5 w-5" /> : <MenuIcon className="h-5 w-5" />}
        </button>

        <img src="/logo.png" alt="Kick Domicilios" className="h-9 w-auto shrink-0" />
        <span className="hidden font-display text-sm uppercase tracking-widest text-white/60 sm:block">
          {admin ? 'Administración' : 'Panel'}
        </span>

        <div className="ml-auto flex items-center gap-2 sm:gap-3">
          {!admin && merchantId && <InterruptorApertura merchantId={merchantId} />}

          <span className="hidden lg:block">
            <HealthBadge />
          </span>

          <Campana />

          <div className="flex items-center gap-2 border-l border-white/15 pl-2 sm:pl-3">
            <span className="hidden text-sm text-white/80 sm:block">{user?.username}</span>
            <button
              onClick={() => salir.mutate()}
              aria-label="Cerrar sesión"
              className="rounded-kick p-2 text-white/80 transition hover:bg-white/10"
            >
              <LogOut className="h-4 w-4" />
            </button>
          </div>
        </div>
      </div>
    </header>
  );
}

/**
 * Interruptor de apertura, siempre visible en la barra.
 *
 * Es la acción más frecuente del día — se abre y se cierra el local — y no
 * debería exigir entrar a una pantalla de configuración.
 */
function InterruptorApertura({ merchantId }: { merchantId: string }) {
  const { data: comercio } = useComercio(merchantId);
  const alternar = useAlternarApertura(merchantId);
  const avisos = useAvisos();

  if (!comercio) return null;

  return (
    <div className="flex items-center gap-2 rounded-full bg-white/10 py-1 pl-3 pr-1.5">
      <span
        className={cn(
          'hidden text-sm font-semibold sm:block',
          comercio.isOpenNow ? 'text-emerald-400' : 'text-white/50',
        )}
      >
        {comercio.isOpenNow ? 'Abierto' : 'Cerrado'}
      </span>
      <Interruptor
        activo={comercio.isOpenNow}
        etiqueta="Abrir o cerrar el comercio"
        disabled={alternar.isPending}
        onCambio={(abierto) =>
          alternar.mutate(abierto, {
            onSuccess: () =>
              avisos.exito(abierto ? 'Comercio abierto, ya reciben pedidos' : 'Comercio cerrado'),
            onError: () => avisos.error('No pudimos cambiar el estado del comercio'),
          })
        }
      />
    </div>
  );
}

function BarraLateral({
  enlaces,
  abierta,
  onCerrar,
}: {
  enlaces: Enlace[];
  abierta: boolean;
  onCerrar: () => void;
}) {
  const { user } = useAuth();
  const admin = esAdmin(user?.role);
  const esEmpleado = user?.role === UserRole.MERCHANT_STAFF;
  const cliente = useQueryClient();

  /**
   * Lo que hay sin leer en soporte, visible desde cualquier pantalla.
   *
   * Un cliente puede escribir mientras se está en liquidaciones o en zonas, y
   * hasta ahora no había forma de enterarse sin entrar a mirar. El número vive
   * en el menú porque el menú está siempre.
   */
  const soporte = useSoporteSinLeer(admin);
  // Y para el comercio, las respuestas de administración que no ha leído.
  const soporteComercio = useSoporteSinLeerComercio(!admin);
  const { pathname } = useLocation();

  /**
   * La alerta de soporte: prende al llegar un mensaje y solo se apaga al
   * ENTRAR al módulo de soporte — no al abrir cada conversación, que es otro
   * asunto (ese es el número de sin leer). Quien está en liquidaciones oye el
   * timbre, ve el aviso latir en el menú, y el aviso lo espera hasta que vaya.
   */
  const [soporteNuevo, setSoporteNuevo] = useState(false);

  useEffect(() => {
    if (pathname.startsWith('/soporte')) setSoporteNuevo(false);
  }, [pathname]);

  /**
   * Y las encomiendas programadas esperando decisión.
   *
   * Una encomienda para el jueves no aparece en Operación —todavía no hay nada
   * que vigilar— así que sin esta marca nadie se entera hasta que alguien
   * entre a mirar por su cuenta. Y llegado el día sin aprobar, no sale.
   */
  const programadas = useEncomiendasPendientes(admin);
  // Recargas de saldo esperando que alguien mire el comprobante.
  const recargas = useRecargasPendientes(admin);
  // Lotes y pedidos a comercios no aliados esperando que alguien elija quién los lleva.
  const manuales = useManualesPendientes(admin);

  // Y en vivo, sin esperar al siguiente minuto: el mensaje llega por el canal.
  useEventoSocket<SupportMessagePayload>(WS_EVENT.SUPPORT_MESSAGE, (mensaje) => {
    void cliente.invalidateQueries({ queryKey: ['admin', 'soporte-sin-leer'] });
    void cliente.invalidateQueries({ queryKey: ['soporte'] });
    // A administración le timbra lo que escriben clientes y comercios; al
    // comercio, solo lo que responde administración.
    const deAdmin = mensaje.senderRole === UserRole.ADMIN;
    if (admin ? deAdmin : !deAdmin) return;
    // Timbra aunque se esté en otra pantalla; la alerta visual solo si no
    // se está ya dentro del soporte, donde sería redundante.
    sonarAviso();
    if (!window.location.pathname.startsWith('/soporte')) setSoporteNuevo(true);
  });

  const sinLeer = admin ? (soporte.data ?? 0) : (soporteComercio.data ?? 0);
  const porDecidir = programadas.data?.length ?? 0;
  const porRecargar = recargas.data?.length ?? 0;
  const porAsignar = (manuales.data?.lotes ?? 0) + (manuales.data?.noAliados ?? 0);

  return (
    <>
      {/* Fondo oscuro en móvil */}
      {abierta && (
        <div className="fixed inset-0 z-20 bg-black/40 lg:hidden" onClick={onCerrar} aria-hidden />
      )}

      <aside
        className={cn(
          /**
           * flex-col + scroll en el menú: la administración ya tiene más
           * secciones de las que caben en una pantalla baja, y sin esto el
           * último enlace (Equipo) quedaba fuera del alcance, sin barra.
           */
          'fixed inset-y-0 left-0 z-20 flex w-64 shrink-0 flex-col border-r border-kick-gray/15 bg-white pt-16 transition-transform lg:sticky lg:top-[57px] lg:z-0 lg:h-[calc(100dvh-57px)] lg:translate-x-0 lg:pt-0',
          abierta ? 'translate-x-0' : '-translate-x-full',
        )}
      >
        <nav aria-label="Secciones del panel" className="flex flex-1 flex-col gap-0.5 overflow-y-auto p-3">
          {enlaces.map(({ a, texto, icono: Icono, fase }) => {
            /**
             * Un empleado no ve las secciones de dinero ni la administración
             * de usuarios: son del propietario.
             */
            if (esEmpleado && (a === '/informes' || a === '/usuarios')) return null;

            if (fase) {
              return (
                <div
                  key={a}
                  className="flex cursor-not-allowed items-center gap-3 rounded-kick px-3 py-2.5 text-sm text-kick-gray/50"
                  title={`Disponible en la fase ${fase}`}
                >
                  <Icono className="h-[18px] w-[18px]" />
                  {texto}
                  <span className="ml-auto rounded-full bg-kick-light px-1.5 py-0.5 text-[10px] font-semibold">
                    F{fase}
                  </span>
                </div>
              );
            }

            return (
              <NavLink
                key={a}
                to={a}
                end={a === '/'}
                onClick={onCerrar}
                className={({ isActive }) =>
                  cn(
                    'flex items-center gap-3 rounded-kick px-3 py-2.5 text-sm font-medium transition',
                    isActive
                      ? 'bg-kick-red/10 text-kick-red'
                      : 'text-kick-ink hover:bg-kick-light',
                  )
                }
              >
                <Icono className="h-[18px] w-[18px]" />
                {texto}
                {a === '/programadas' && porDecidir > 0 && (
                  <span
                    className="ml-auto grid h-5 min-w-[1.25rem] place-items-center rounded-full bg-kick-red px-1.5 text-[11px] font-bold text-white"
                    aria-label={`${porDecidir} ${porDecidir === 1 ? 'encomienda espera' : 'encomiendas esperan'} decisión`}
                  >
                    {porDecidir > 9 ? '9+' : porDecidir}
                  </span>
                )}
                {a === '/manuales' && porAsignar > 0 && (
                  <span
                    className="ml-auto grid h-5 min-w-[1.25rem] place-items-center rounded-full bg-kick-red px-1.5 text-[11px] font-bold text-white"
                    aria-label={`${porAsignar} esperan asignación manual`}
                  >
                    {porAsignar > 9 ? '9+' : porAsignar}
                  </span>
                )}
                {a === '/domiciliarios' && porRecargar > 0 && (
                  <span
                    className="ml-auto grid h-5 min-w-[1.25rem] place-items-center rounded-full bg-kick-red px-1.5 text-[11px] font-bold text-white"
                    aria-label={`${porRecargar} ${porRecargar === 1 ? 'recarga espera' : 'recargas esperan'} revisión`}
                  >
                    {porRecargar > 9 ? '9+' : porRecargar}
                  </span>
                )}
                {a === '/soporte' && (sinLeer > 0 || soporteNuevo) && (
                  <span
                    className={cn(
                      'ml-auto grid h-5 min-w-[1.25rem] place-items-center rounded-full bg-kick-red px-1.5 text-[11px] font-bold text-white',
                      // Late mientras el mensaje nuevo siga sin atenderse.
                      soporteNuevo && 'animate-pulse ring-2 ring-kick-red/40',
                    )}
                    /* El número solo no dice de qué: quien navega a oídas
                       escucharía «Soporte, 3» sin saber 3 de qué. */
                    aria-label={
                      sinLeer > 0
                        ? `${sinLeer} ${sinLeer === 1 ? 'conversación' : 'conversaciones'} sin leer`
                        : 'Mensaje nuevo en soporte'
                    }
                  >
                    {sinLeer > 9 ? '9+' : sinLeer > 0 ? sinLeer : '•'}
                  </span>
                )}
              </NavLink>
            );
          })}
        </nav>

        <div className="mt-auto border-t border-kick-gray/15 p-4">
          <div className="flex items-center gap-2 text-xs text-kick-gray">
            {admin ? <ShieldCheck className="h-4 w-4" /> : <Store className="h-4 w-4" />}
            <span className="truncate">{user?.fullName}</span>
          </div>
        </div>
      </aside>
    </>
  );
}
